import type { TFunction } from "i18next";
import { DateTime } from "luxon";

import type { EventLogEntry } from "@core/apis/shared";

export type ChangeSide = "from" | "to";

/**
 * Radiator-curve breakpoint with before/after feed temperatures.
 *
 * `null` means that side of the change has no breakpoint at this outdoor
 * temperature.
 */
export type MergedBreakpoint = {
  outdoor: number;
  feedFrom: number | null;
  feedTo: number | null;
};

/** Sentinel used by the type filter to represent "show every event type". */
export const ALL_EVENT_TYPES = "ALL";

/** "radiator_system_supply_temp" -> "Radiator system supply temp" */
export function humanizeToken(token: string): string {
  const text = token.replaceAll("_", " ").trim().toLowerCase();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatTemperature(value: number): string {
  return `${formatNumber(value)} \u00b0C`;
}

function formatTemperatureRange(min: number, max: number): string {
  return `${formatNumber(min)}\u2013${formatNumber(max)} \u00b0C`;
}

/** Localised, human readable timestamp (e.g. "Jun 15, 2026, 8:54 AM"). */
export function formatEventTimestamp(timestamp: string): string {
  const dt = DateTime.fromISO(timestamp);
  return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED) : timestamp;
}

/** Translated event type label, falling back to a humanized raw value. */
export function formatEventType(t: TFunction, type: string): string {
  return t(`events.type.${type.toLowerCase()}`, { defaultValue: humanizeToken(type) });
}

/** Who triggered the change. `null` (system generated) -> "System". */
export function formatChangedBy(t: TFunction, entry: EventLogEntry): string {
  return entry.changed_by?.display_name?.trim() || t("events.system");
}

/** Origin/source of the change (DH employee, Utilifeed, ...). */
export function formatEventSource(t: TFunction, entry: EventLogEntry): string {
  const source = entry.changed_by?.type;
  if (!source) return t("events.source.system");
  return t(`events.source.${source.toLowerCase()}`, { defaultValue: humanizeToken(source) });
}

function formatToggle(
  state: { enabled: boolean } | null | undefined,
  onLabel: string,
  offLabel: string,
  notAvailable: string
): string {
  if (!state) return notAvailable;
  return state.enabled ? onLabel : offLabel;
}

function formatSensorAdded(entry: EventLogEntry, side: ChangeSide, notAvailable: string): string {
  const sensor = entry.sensor_added;
  if (side === "from" || !sensor) return notAvailable;
  const typeLabel = humanizeToken(sensor.sensor_type);
  return sensor.sensor_name?.trim() ? `${typeLabel} (${sensor.sensor_name})` : typeLabel;
}

/** Display value for the "changed from" / "changed to" columns. */
export function formatEventChange(t: TFunction, entry: EventLogEntry, side: ChangeSide): string {
  const notAvailable = t("events.not_available");

  switch (entry.type) {
    case "LOCAL_CONTROL_CHANGE":
      return formatToggle(
        entry.local_control_change?.[side],
        t("events.enabled"),
        t("events.disabled"),
        notAvailable
      );
    case "SENSOR_ENABLED_CHANGE":
      return formatToggle(
        entry.sensor_enabled_change?.[side],
        t("events.included"),
        t("events.excluded"),
        notAvailable
      );
    case "FLEX_CONTROL_CHANGE":
      return formatToggle(
        entry.flex_control_change?.[side],
        t("events.enabled"),
        t("events.disabled"),
        notAvailable
      );
    case "TARGET_TEMPERATURE_CHANGE": {
      const state = entry.target_temperature_change?.[side];
      if (!state) return notAvailable;
      // Only `min` is shown — `max` is assumed to never change for target temperature.
      return formatTemperature(state.min);
    }
    case "RADIATOR_CURVE_CHANGE": {
      const state = entry.radiator_curve_change?.[side];
      if (!state) return notAvailable;
      return formatTemperatureRange(state.min_feed_temperature, state.max_feed_temperature);
    }
    case "SENSOR_ADDED":
      return formatSensorAdded(entry, side, notAvailable);
    default:
      return notAvailable;
  }
}

/**
 * Merge a radiator curve change's `from`/`to` breakpoints by outdoor temperature so the
 * detail panel can show the feed temperature before and after the change side by side.
 * Returns one entry per outdoor temperature, sorted ascending.
 */
export function getMergedBreakpoints(entry: EventLogEntry): MergedBreakpoint[] {
  if (entry.type !== "RADIATOR_CURVE_CHANGE") return [];
  const change = entry.radiator_curve_change;

  const byOutdoor = new Map<number, MergedBreakpoint>();
  const ensure = (outdoor: number): MergedBreakpoint => {
    let merged = byOutdoor.get(outdoor);
    if (!merged) {
      merged = { outdoor, feedFrom: null, feedTo: null };
      byOutdoor.set(outdoor, merged);
    }
    return merged;
  };

  for (const [outdoor, feed] of change?.from?.breakpoints ?? []) {
    ensure(outdoor).feedFrom = feed;
  }
  for (const [outdoor, feed] of change?.to?.breakpoints ?? []) {
    ensure(outdoor).feedTo = feed;
  }

  return Array.from(byOutdoor.values()).sort((a, b) => a.outdoor - b.outdoor);
}

/** Whether a row has an expandable breakpoints detail panel. */
export function hasEventBreakpoints(entry: EventLogEntry): boolean {
  if (entry.type !== "RADIATOR_CURVE_CHANGE") return false;
  const change = entry.radiator_curve_change;
  return (change?.from?.breakpoints?.length ?? 0) > 0 || (change?.to?.breakpoints?.length ?? 0) > 0;
}

/** Format a feed temperature for the detail panel; `null` renders as an em dash. */
export function formatFeedTemperature(value: number | null): string {
  return value === null ? "—" : formatNumber(value);
}
